// Cmd / Ctrl + K command palette. Searches all resources for the current
// provider; selecting one enters click-to-place mode (mirrors the sidebar).

import { useEffect, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";
import { categoryToTier } from "../../lib/archReducer";

export default function CommandSearch({ provider, open, onOpenChange, onPick }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || !provider) return;
    let cancelled = false;
    api
      .get(`/cloud-resources?provider=${provider}`)
      .then(({ data }) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [open, provider]);

  // Build category -> items map (memoized through items by reference).
  const grouped = (() => {
    const by = new Map();
    for (const it of items) {
      const k = it.category || "Other";
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(it);
    }
    return Array.from(by.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  })();

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Search resources</DialogTitle>
      </VisuallyHidden>
      <CommandInput
        placeholder="Search resources to add…"
        data-testid="command-search-input"
      />
      <CommandList data-testid="command-search-list">
        <CommandEmpty>No matching resources.</CommandEmpty>
        {grouped.map(([cat, list]) => (
          <CommandGroup key={cat} heading={cat}>
            {list.map((it) => (
              <CommandItem
                key={`${cat}-${it.name}`}
                value={`${it.name} ${cat} ${it.description || ""}`}
                data-testid={`command-item-${it.name}`}
                onSelect={() => {
                  onPick?.({
                    kind: "new-resource",
                    name: it.name,
                    category: cat,
                    tier: categoryToTier(cat),
                    description: it.description || "",
                  });
                  onOpenChange?.(false);
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }} className="truncate">
                  {it.name}
                </span>
                <span
                  className="font-mono"
                  style={{
                    fontSize: 10,
                    color: "var(--color-text-tertiary)",
                    marginLeft: 8,
                  }}
                >
                  {cat}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
